import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/th';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { SERVICE_URL } from '../../utils/api';
import shrinkImage from './shrinkImage';
import LaundryWork from './LaundryWork';
import DepositWork from './DepositWork';
import DepositManage from './DepositManage';
import MaintenanceWork from './MaintenanceWork';
import StockWork from './StockWork';
import PayoutDetail from './PayoutDetail';
import { getCleanerToken, setUrlParams, NO_TOKEN_MESSAGE } from './cleanerToken';

const STATUS = {
  pending: { label: 'รอเตรียม', color: '#8A938B' },
  acknowledged: { label: 'รับทราบแล้ว', color: '#8A938B' },
  room_ready: { label: 'ห้องพร้อม', color: '#2C6DAF' },
  guest_checkedin: { label: 'รออนุมัติ', color: '#B26B0E' },
  pending_approval: { label: 'รออนุมัติ', color: '#B26B0E' },
  approved: { label: 'อนุมัติแล้ว', color: '#0F8A56' },
  paid: { label: 'จ่ายแล้ว', color: '#0F8A56' },
  declined: { label: 'ไม่สะดวก', color: '#CD3E33' },
};
const statusOf = (s) => STATUS[s] || { label: s, color: '#8A938B' };
// แม่บ้านรายเดือน: ส่งงานแล้ว = รายงานแล้ว จบ (API ปิดงานเป็น payment_status = void ไม่มีอนุมัติ/จ่าย)
const jobStatusOf = (job) => (job.payment_status === 'void' && ['guest_checkedin', 'pending_approval'].includes(job.status)
  ? { label: 'รายงานแล้ว', color: '#0F8A56' }
  : statusOf(job.status));

// ระดับบริการระหว่างพัก (midstay) — แม่บ้านเลือกเอง
const MIDSTAY_LEVELS = [
  { v: 1, label: 'ลูกค้าไม่ให้ทำอะไร' },
  { v: 2, label: 'ทำความสะอาดด้านนอก + ห้องน้ำ' },
  { v: 3, label: '+ เปลี่ยนผ้าปู / ผ้าเช็ดตัว' },
  { v: 4, label: 'ทำความสะอาด + เปลี่ยนอุปกรณ์ครบ' },
];
// ระบบซักผ้า: แม่บ้านบอกว่าเปลี่ยนผ้าไหม (โชว์เฉพาะที่พักที่นับผ้าระหว่างพัก -- me.laundry_midstay)
const LINEN_CHANGES = [
  { v: 'none', label: 'ไม่เปลี่ยน' },
  { v: 'towel', label: 'เฉพาะผ้าเช็ดตัว' },
  { v: 'full', label: 'เปลี่ยนทั้งชุด' },
];
const linenLabel = (v) => (LINEN_CHANGES.find((l) => l.v === v) || {}).label;
const midstayLevelLabel = (v) => (MIDSTAY_LEVELS.find((l) => l.v === Number(v)) || {}).label || `ระดับ ${v}`;

// งานทีม (ห้องที่มีผู้ช่วย): ยอดทีมเท่าเดิม ส่วนของคนที่ไม่ได้ทำไปให้คนที่ทำ ตามสัดส่วนเรต
// (สูตรเดียวกับ API computeShares -- ใช้โชว์ตัวเลขก่อนกดส่ง ยอดจริงคิดที่ server)
// คนช่วยเพิ่ม (คนนอก): เรตตัวเองถ้ามา / 0 ถ้าไม่มา (ไม่แบ่งกับทีม)
const estimateShares = (members, worked) => {
  const out = {};
  members.forEach((m) => { out[m.cleaner_id] = 0; });
  members.filter((m) => m.is_extra).forEach((m) => {
    out[m.cleaner_id] = worked[m.cleaner_id] !== false && m.wage_type !== 'monthly' ? Number(m.rate_snapshot || 0) : 0;
  });
  const teamMembers = members.filter((m) => !m.is_extra);
  const total = teamMembers.reduce((s, m) => s + Number(m.rate_snapshot || 0), 0);
  const ok = teamMembers.filter((m) => worked[m.cleaner_id] !== false && m.wage_type !== 'monthly');
  const w = ok.reduce((s, m) => s + Number(m.rate_snapshot || 0), 0);
  if (!ok.length || total <= 0) return out;
  let given = 0;
  ok.forEach((m) => { const v = Math.floor(total * (w > 0 ? Number(m.rate_snapshot || 0) / w : 1 / ok.length)); out[m.cleaner_id] = v; given += v; });
  const first = ok.find((m) => m.is_lead) || ok[0];
  out[first.cleaner_id] += Math.round((total - given) * 100) / 100;
  return out;
};
const baht = (v) => `฿${Number(v || 0).toLocaleString()}`;
const PAY_MODE_TEXT = { cycle: 'จ่ายเป็นรอบ', on_request: 'สะสม · กดขอเบิกได้ทุกเมื่อ' };

const MAX_PHOTOS = 5; // ต่อ 1 ข้อ (API เช็คซ้ำ)
const photosOf = (c) => (c?.photo_urls?.length ? c.photo_urls : (c?.photo_url ? [c.photo_url] : []));

// ร่างงานที่ทำค้างไว้ -- LINE บน Android มักรีโหลดหน้าเว็บตอนกลับจากกล้อง (เครื่องคืนหน่วยความจำ)
// ถ้าไม่เก็บไว้ แม่บ้านต้องเช็ค/ถ่ายใหม่ทั้งหมด · localStorage อาจใช้ไม่ได้ (private mode) -> ทำงานต่อได้แค่ไม่จำ
const draftKey = (jobId) => `cleaner-draft-${jobId}`;
const loadDraft = (jobId) => { try { return JSON.parse(window.localStorage.getItem(draftKey(jobId)) || 'null'); } catch (e) { return null; } };
const saveDraft = (jobId, data) => { try { window.localStorage.setItem(draftKey(jobId), JSON.stringify(data)); } catch (e) { /* ignore */ } };
const clearDraft = (jobId) => { try { window.localStorage.removeItem(draftKey(jobId)); } catch (e) { /* ignore */ } };

// จำงานที่เปิดอยู่ไว้ใน URL -- หน้ารีโหลดแล้วกลับมาที่งานเดิมเลย ไม่ต้องไล่หาใหม่
const setUrlJob = (jobId) => {
  try {
    const q = new URLSearchParams(window.location.search);
    if (jobId) q.set('job', jobId); else q.delete('job');
    window.history.replaceState(null, '', `${window.location.pathname}?${q.toString()}`);
  } catch (e) { /* ignore */ }
};

export default function CleanerWork() {
  const search = new URLSearchParams(window.location.search);
  const token = getCleanerToken(); // ?t= หรือรหัสที่เคยเปิดไว้ (การ์ดกลุ่มไลน์ไม่มี t)
  const initialView = ['deposit', 'deposit-manage', 'maintenance', 'stock'].includes(search.get('view')) ? search.get('view') : null;
  const initialDate = initialView ? null : search.get('date'); // หน้าเงินประกันใช้ date ของตัวเอง
  const initialJob = initialView ? null : search.get('job');

  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);
  const [view, setView] = useState(initialView || 'calendar'); // calendar | day | job | deposit | deposit-manage | stock | payout
  const [activeMonth, setActiveMonth] = useState(initialDate ? new Date(initialDate) : new Date());
  const [monthBookings, setMonthBookings] = useState([]); // booking ทั้งเดือน (occupancy) อิงห้องของแม่บ้าน
  const [selectedDate, setSelectedDate] = useState(initialDate || null);
  const [job, setJob] = useState(null);
  const [phase, setPhase] = useState('prep');
  const [template, setTemplate] = useState([]);
  const [checks, setChecks] = useState({});
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState({}); // { [item_id]: จำนวนรูปที่กำลังอัปโหลด }
  const [triedSubmit, setTriedSubmit] = useState(false); // กดส่งแล้วแต่รูปยังไม่ครบ -> ไฮไลต์ข้อที่ขาด
  const [midstayLevel, setMidstayLevel] = useState(null); // ระดับบริการที่แม่บ้านเลือก (midstay)
  const [midstayNote, setMidstayNote] = useState('');
  const [linenChange, setLinenChange] = useState(null); // none | towel | full (midstay)
  const [dirtySets, setDirtySets] = useState('1');      // ฟอร์ม "+ ผ้าเลอะ"
  const [dirtyNote, setDirtyNote] = useState('');
  const [worked, setWorked] = useState({}); // งานทีม: { [cleaner_id]: true/false } -- ค่าเริ่มต้น ทุกคนทำ
  const [balances, setBalances] = useState([]); // ยอดสะสมของทุกคนในทีม (เฉพาะโหมดรอบ/ขอเบิก)
  const [walletOf, setWalletOf] = useState(null); // cleaner_id ที่เปิดดูประวัติรับเงิน
  const [payouts, setPayouts] = useState([]);
  const [payoutId, setPayoutId] = useState(null); // ใบจ่ายที่เปิดดูรายละเอียด (view = payout)

  const api = axios.create({ baseURL: SERVICE_URL });
  const cw = useCallback((path, opts = {}) => {
    const sep = path.includes('?') ? '&' : '?';
    return api({ url: `cleaner-web/${path}${sep}t=${encodeURIComponent(token || '')}`, ...opts });
    // eslint-disable-next-line
  }, [token]);

  const loadCalendar = useCallback((monthDate) => {
    const month = moment(monthDate).format('YYYY-MM');
    cw(`my-overview?month=${month}`).then((r) => {
      setMonthBookings(Array.isArray(r.data) ? r.data : []);
    }).catch(() => {});
  }, [cw]);

  const loadBalance = useCallback(() => {
    cw('my-balance').then((r) => setBalances(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [cw]);

  const openDay = useCallback((date) => {
    setSelectedDate(date);
    setView('day');
    setUrlJob(null);
  }, []);

  useEffect(() => {
    if (!token) { setError(NO_TOKEN_MESSAGE); return; }
    cw('me').then((r) => {
      setMe(r.data);
      if (initialView) return; // เปิดตรงหน้าเงินประกัน (โหลดข้อมูลเอง) -- ปฏิทินโหลดตอนกดกลับ
      if (r.data?.role === 'laundry') return; // คนซักผ้า -> หน้า LaundryWork (โหลดข้อมูลของตัวเอง)
      if (r.data?.role === 'technician') return; // ช่าง -> หน้า MaintenanceWork (โหลดข้อมูลของตัวเอง)
      loadCalendar(activeMonth);
      loadBalance();
      if (initialDate) openDay(initialDate);
      if (initialJob) openJob(initialJob); // กลับมาจากหน้ารีโหลด (ดู setUrlJob)
    }).catch(() => setError('ลิงก์ไม่ถูกต้องหรือหมดอายุ กรุณาเปิดจากลิงก์ในไลน์อีกครั้ง'));
    // eslint-disable-next-line
  }, []);

  useEffect(() => { if (token && me && !['laundry', 'technician'].includes(me.role)) loadCalendar(activeMonth); }, [activeMonth]); // eslint-disable-line

  const openJob = (id) => {
    cw(`job/${id}`).then((r) => {
      setJob(r.data.job); setPhase(r.data.phase); setTemplate(r.data.checklist_template || []);
      // ร่างที่ค้างไว้ใช้ได้เฉพาะ phase เดียวกัน (ส่ง prep แล้ว -> ร่าง prep ไม่เกี่ยวกับ checkin)
      const draft = loadDraft(id);
      const d = draft && draft.phase === r.data.phase ? draft : null;
      const init = {};
      (r.data.checklist_template || []).forEach((it) => { init[it.id] = { result: 'pass', note: '', photos: [], ...(d?.checks?.[it.id] || {}) }; });
      setChecks(init);
      setMidstayLevel(r.data.job?.service_level || d?.midstayLevel || null); // ถ้าเคยส่งแล้วจะโชว์ระดับเดิม
      setMidstayNote(r.data.job?.cleaner_note || d?.midstayNote || '');
      setLinenChange(r.data.job?.linen_change || d?.linenChange || null);
      const w = {};
      (r.data.job?.members || []).forEach((m) => { w[m.cleaner_id] = d?.worked?.[m.cleaner_id] ?? m.worked !== false; });
      setWorked(w);
      setUploading({});
      setTriedSubmit(false);
      setView('job');
      setUrlJob(id);
    }).catch(() => {});
  };

  const acknowledge = (id) => cw(`job/${id}/acknowledge`, { method: 'post' }).then(() => { loadCalendar(activeMonth); openDay(selectedDate); }).catch(() => {});

  // ----- derive จาก monthBookings: ปฏิทิน occupancy + รายการของวันที่เลือก -----
  const calMap = useMemo(() => {
    const map = {};
    const bump = (ds, key) => { if (!map[ds]) map[ds] = { occ: 0, arrivals: 0, checkouts: 0 }; map[ds][key] += 1; };
    monthBookings.forEach((b) => {
      if (!b.start_date || !b.end_date) return;
      let d = moment(b.start_date); const end = moment(b.end_date); let guard = 0;
      while (d.isSameOrBefore(end, 'day') && guard < 120) {
        const ds = d.format('YYYY-MM-DD');
        bump(ds, 'occ');
        if (ds === b.start_date) bump(ds, 'arrivals'); // แขกเข้าใหม่วันนั้น = ต้องเตรียมห้อง
        d = d.add(1, 'day'); guard += 1;
      }
      bump(moment(b.end_date).add(1, 'days').format('YYYY-MM-DD'), 'checkouts'); // วันเช็คเอาท์ = end_date + 1 (มาเก็บห้อง)
    });
    return map;
  }, [monthBookings]);

  const dayBookings = useMemo(() => {
    if (!selectedDate) return [];
    return monthBookings
      .filter((b) => {
        if (!b.start_date || !b.end_date) return false;
        const co = moment(b.end_date).add(1, 'days').format('YYYY-MM-DD');
        return (b.start_date <= selectedDate && selectedDate <= b.end_date) || selectedDate === co; // พักอยู่ หรือ เช็คเอาท์วันนี้
      })
      // เช็คเอาท์ (เก็บห้องคนเก่า) ขึ้นก่อน แล้วค่อยงานเข้าพัก/เตรียมห้องใหม่ · จากนั้นเรียงตามชื่อห้อง
      .sort((a, b) => {
        const co = (x) => (moment(x.end_date).add(1, 'days').format('YYYY-MM-DD') === selectedDate ? 0 : 1);
        return (co(a) - co(b)) || (a.room?.name_th || '').localeCompare(b.room?.name_th || '');
      });
  }, [monthBookings, selectedDate]);

  const setResult = (id, result) => setChecks((c) => ({ ...c, [id]: { ...c[id], result } }));
  const setNote = (id, note) => setChecks((c) => ({ ...c, [id]: { ...c[id], note } }));
  const addUploading = (id, n) => setUploading((u) => ({ ...u, [id]: Math.max(0, (u[id] || 0) + n) }));

  // อัปโหลดทีละรูป (ไม่ยิงพร้อมกัน) -- เน็ตมือถืออ่อนๆ ยิงพร้อมกัน 5 รูปจะ timeout ทั้งชุด
  const onPickPhotos = async (id, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const room = MAX_PHOTOS - (checks[id]?.photos || []).length - (uploading[id] || 0);
    if (room <= 0) return;
    if (files.length > room) alert(`ข้อนี้แนบได้อีก ${room} รูป — จะใช้ ${room} รูปแรก`);
    const picked = files.slice(0, room);
    addUploading(id, picked.length);
    let failed = 0;
    for (const file of picked) { // eslint-disable-line no-restricted-syntax
      try {
        const small = await shrinkImage(file); // eslint-disable-line no-await-in-loop
        const fd = new FormData(); fd.append('file', small, 'photo.jpg');
        const r = await cw('upload-photo', { method: 'post', data: fd, timeout: 90000 }); // eslint-disable-line no-await-in-loop
        setChecks((c) => ({ ...c, [id]: { ...c[id], photos: [...(c[id]?.photos || []), r.data.url].slice(0, MAX_PHOTOS) } }));
      } catch (e) { failed += 1; }
      addUploading(id, -1);
    }
    if (failed) alert(`ส่งรูปไม่สำเร็จ ${failed} รูป\nสัญญาณเน็ตอาจไม่ดี — กดถ่ายรูปใหม่อีกครั้ง`);
  };

  const removePhoto = (id, url) => {
    if (!window.confirm('ลบรูปนี้?')) return;
    setChecks((c) => ({ ...c, [id]: { ...c[id], photos: (c[id]?.photos || []).filter((u) => u !== url) } }));
  };

  // midstay: แสดง/ส่ง checklist เฉพาะที่อยู่ในระดับที่เลือก (ระดับ 1 = ไม่มีเลย)
  const visibleTemplate = phase === 'midstay'
    ? (midstayLevel ? template.filter((it) => (Number(it.service_level) || 99) <= midstayLevel) : [])
    : template;
  const missingPhotoItems = visibleTemplate.filter((it) => it.require_photo && !(checks[it.id]?.photos || []).length);
  const anyUploading = Object.values(uploading).some((n) => n > 0);
  const jobDone = ['guest_checkedin', 'pending_approval', 'approved', 'paid'].includes(job?.status);

  // เก็บร่างทุกครั้งที่แก้ (ดู loadDraft)
  useEffect(() => {
    if (view !== 'job' || !job || jobDone) return;
    saveDraft(job.id, { phase, checks, midstayLevel, midstayNote, linenChange, worked });
  }, [checks, midstayLevel, midstayNote, linenChange, worked]); // eslint-disable-line

  const toChecklist = (items) => items.map((it) => ({
    item_id: it.id, label: it.label,
    result: checks[it.id]?.result || 'pass',
    note: checks[it.id]?.note || '',
    // รูปของข้อไม่บังคับที่กดกลับเป็น ✓ ซ่อนอยู่ -> ไม่ส่ง
    photo_urls: (it.require_photo || checks[it.id]?.result === 'fail') ? (checks[it.id]?.photos || []) : [],
  }));

  // ส่งไม่ผ่าน -> บอกเหตุผลเสมอ (เดิมเงียบ แม่บ้านกดซ้ำไม่รู้ว่าทำไมไม่ไป)
  const onSubmitError = (e) => {
    setBusy(false);
    alert(e?.response?.data?.message || 'ส่งงานไม่สำเร็จ — สัญญาณเน็ตอาจไม่ดี ลองกดส่งอีกครั้ง');
  };
  const onSubmitDone = () => {
    clearDraft(job.id);
    setBusy(false); loadCalendar(activeMonth); loadBalance(); openDay(selectedDate);
  };

  // งานทีม: ส่งรายชื่อคนที่ทำ (เฉพาะขั้นที่เกิดค่าจ้าง: ต้อนรับลูกค้า / ระหว่างพัก)
  const teamMembers = job?.members || [];
  // คนช่วยเพิ่ม (คนนอก) เกิดยอดตอนห้องพร้อม -> ถามตั้งแต่ขั้นเตรียมห้อง
  const hasExtras = teamMembers.some((m) => m.is_extra);
  const askTeam = teamMembers.length > 0 && (phase === 'checkin' || phase === 'midstay' || (phase === 'prep' && hasExtras));
  const membersPayload = () => (askTeam ? teamMembers.map((m) => ({ cleaner_id: m.cleaner_id, worked: worked[m.cleaner_id] !== false })) : undefined);
  const teamReady = () => {
    if (askTeam && !teamMembers.some((m) => !m.is_extra && worked[m.cleaner_id] !== false)) { alert('ต้องมีคนในทีมทำงานอย่างน้อย 1 คน'); return false; }
    return true;
  };

  // เช็คก่อนส่ง: รูปยังอัปโหลดอยู่ / ข้อบังคับถ่ายรูปยังไม่มีรูป -> บอก + เลื่อนไปข้อนั้น
  const photosReady = () => {
    if (anyUploading) { alert('รอรูปส่งเสร็จก่อนนะคะ'); return false; }
    if (missingPhotoItems.length) {
      setTriedSubmit(true);
      alert(`ยังไม่ได้ถ่ายรูป ${missingPhotoItems.length} ข้อ:\n${missingPhotoItems.map((it) => `• ${it.label}`).join('\n')}`);
      const el = document.getElementById(`ck-${missingPhotoItems[0].id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }
    return true;
  };

  const submit = () => {
    if (!job || busy) return;
    if (phase === 'midstay') return submitMidstay();
    if (!photosReady() || !teamReady()) return;
    setBusy(true);
    const ep = phase === 'checkin' ? 'checkin' : 'prep';
    cw(`job/${job.id}/${ep}`, { method: 'post', data: { checklist: toChecklist(template), members: membersPayload() } })
      .then(onSubmitDone).catch(onSubmitError);
  };

  // ส่งงาน midstay: เลือกระดับ + เฉพาะ checklist ที่อยู่ในระดับนั้น + โน้ต
  const submitMidstay = () => {
    if (!midstayLevel || busy) return;
    if (!photosReady() || !teamReady()) return;
    setBusy(true);
    cw(`job/${job.id}/midstay`, { method: 'post', data: { service_level: midstayLevel, checklist: toChecklist(visibleTemplate), note: midstayNote, linen_change: linenChange || undefined, members: membersPayload() } })
      .then(onSubmitDone).catch(onSubmitError);
  };

  const anyFail = template.some((it) => checks[it.id]?.result === 'fail');

  // ผ้าเลอะเหตุอื่น -> เข้ารอบส่งซักที่เปิดอยู่
  const submitDirty = () => {
    const n = Number(dirtySets);
    if (!(n > 0) || busy) { if (!(n > 0)) alert('กรุณาใส่จำนวนชุด'); return; }
    setBusy(true);
    cw('laundry/add', { method: 'post', data: { sets: n, note: dirtyNote } })
      .then(() => { setBusy(false); setDirtySets('1'); setDirtyNote(''); alert('บันทึกแล้ว ✓'); setView('calendar'); })
      .catch(onSubmitError);
  };

  // ขอเบิก (ทีมใช้ลิงก์หัวหน้า -> เบิกแทนคนในทีมได้)
  const withdraw = (person) => {
    if (busy) return;
    const name = person.cleaner?.firstname || '';
    if (!window.confirm(`ขอเบิกเงินของ ${name} ${baht(person.available)} ?`)) return;
    setBusy(true);
    cw('withdraw', { method: 'post', data: { cleaner_id: person.cleaner.id } })
      .then(() => { setBusy(false); alert('ส่งคำขอแล้ว ✓ รอแอดมินโอนเงิน'); loadBalance(); })
      .catch(onSubmitError);
  };
  const openWallet = (cleanerId) => {
    setWalletOf(cleanerId); setPayouts([]); setView('wallet');
    cw(`my-payouts?cleaner_id=${cleanerId}`).then((r) => setPayouts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  };
  // โชว์การ์ดยอดสะสมเฉพาะคนที่ไม่ได้จ่ายทีละงาน (per_job = เหมือนเดิม ไม่มีการ์ด)
  const walletPeople = balances.filter((p) => p.pay_mode && p.pay_mode !== 'per_job');

  // ---------- styles ----------
  const S = {
    // paddingTop เผื่อ header ของ LINE in-app browser ที่อยู่ชิดด้านบน (แยก property กัน shorthand+comma พัง)
    page: { boxSizing: 'border-box', width: '100%', maxWidth: 480, margin: '0 auto', paddingTop: 76, paddingRight: 16, paddingBottom: 48, paddingLeft: 16, fontFamily: 'inherit', color: '#1A211D', minHeight: '100vh', overflowX: 'hidden' },
    bar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 },
    back: { flexShrink: 0, border: '1px solid #DBE2DC', background: '#fff', borderRadius: 10, padding: '8px 12px', fontWeight: 700, color: '#0F8A56', whiteSpace: 'nowrap' },
    h: { fontSize: 20, fontWeight: 800, margin: 0 },
    sub: { fontSize: 13, color: '#586159' },
    card: { boxSizing: 'border-box', border: '1.5px solid #DBE2DC', borderRadius: 14, padding: 16, marginBottom: 12, background: '#fff' },
    bigBtn: (bg) => ({ boxSizing: 'border-box', width: '100%', border: 'none', borderRadius: 16, padding: 20, fontSize: 20, fontWeight: 800, color: '#fff', background: bg, marginTop: 12 }),
    toggleWrap: { display: 'flex', border: '1.5px solid #CBD4CC', borderRadius: 999, overflow: 'hidden' },
    tg: (on, kind) => ({ border: 'none', padding: '10px 16px', fontSize: 16, fontWeight: 800, minWidth: 56, background: on ? (kind === 'pass' ? '#0F8A56' : '#B26B0E') : 'transparent', color: on ? '#fff' : '#8A938B' }),
    pill: (c) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 999, fontSize: 13, fontWeight: 700, color: c, background: c + '22' }),
  };

  if (error) {
    return <div style={{ ...S.page, textAlign: 'center', paddingTop: 80 }}>
      <div style={{ fontSize: 40 }}>🔒</div>
      <p style={{ marginTop: 12, color: '#586159' }}>{error}</p>
    </div>;
  }
  if (!me) return <div style={{ ...S.page, textAlign: 'center', paddingTop: 80, color: '#586159' }}>กำลังโหลด…</div>;

  // ---------- เงินประกัน (DepositWork = รับเงิน/ตรวจห้อง · DepositManage = ผู้ดูแลเงินคืนเงิน) ----------
  const backFromDeposit = () => {
    setUrlParams({ view: null, d: null, date: null });
    setView('calendar');
    if (me.role !== 'laundry') { loadCalendar(activeMonth); loadBalance(); }
  };
  if (view === 'deposit' || view === 'deposit-manage') {
    const allowed = view === 'deposit' ? me.deposit_enabled : me.can_manage_deposit;
    if (!allowed) {
      return <div style={{ ...S.page, textAlign: 'center', paddingTop: 80 }}>
        <div style={{ fontSize: 40 }}>🔒</div>
        <p style={{ marginTop: 12, color: '#586159' }}>
          {view === 'deposit' ? 'ที่พักนี้ยังไม่เปิดใช้ระบบเงินประกัน' : `บัญชีของ${me.firstname || 'คุณ'} ไม่มีสิทธิ์คืนเงินประกัน — ถ้าคุณเป็นผู้ดูแลเงิน ให้${NO_TOKEN_MESSAGE}`}
        </p>
        <button style={{ ...S.back, marginTop: 12 }} onClick={backFromDeposit}>‹ กลับหน้าหลัก</button>
      </div>;
    }
    return view === 'deposit'
      ? <DepositWork cw={cw} S={S} onBack={backFromDeposit} token={token} stockEnabled={!!me.stock_enabled} />
      : <DepositManage cw={cw} S={S} onBack={backFromDeposit} />;
  }

  // ---------- ระบบคลังสินค้า: แจ้งของเสีย/หาย · รายการหักของฉัน (ลิงก์ไลน์ ?view=stock&incident=<id>) ----------
  // อยู่ก่อนเช็ค role -- ลิงก์รายการหักเปิดได้ทุกคน (StockWork เช็คว่าที่พักเปิดระบบคลังสินค้าเอง)
  if (view === 'stock') {
    const backFromStock = () => {
      setUrlParams({ view: null, tab: null, incident: null });
      setView('calendar');
      cw('me').then((r) => setMe(r.data)).catch(() => {}); // อัปเดตจำนวนรายการรอยอมรับ
      if (!['laundry', 'technician'].includes(me.role)) { loadCalendar(activeMonth); loadBalance(); }
    };
    return <StockWork token={token} cw={cw} S={S} onBack={backFromStock} />;
  }

  // คนซักผ้า (role = laundry) ใช้ลิงก์ชุดเดียวกัน แต่เห็นหน้างานรับ-ส่งผ้าแทนปฏิทินแม่บ้าน
  if (me.role === 'laundry') return <LaundryWork me={me} cw={cw} S={S} />;

  // ช่างซ่อมบำรุง (role = technician) -> หน้างานซ่อมเป็นหน้าหลัก
  // แม่บ้าน -> view=maintenance = แจ้งปัญหา / ดูงานซ่อมที่แจ้ง (ลิงก์ในกลุ่มไลน์ก็เปิดมาที่นี่)
  if (me.role === 'technician') return <MaintenanceWork token={token} S={S} />;
  if (view === 'maintenance') {
    const backFromMaintenance = () => {
      setUrlParams({ view: null, ticket: null });
      setView('calendar');
      loadCalendar(activeMonth); loadBalance();
    };
    return <MaintenanceWork token={token} S={S} onBack={backFromMaintenance} />;
  }

  // ---------- + ผ้าเลอะ (ระบบซักผ้า) ----------
  if (view === 'laundry-add') {
    const inp = { boxSizing: 'border-box', width: '100%', border: '1.5px solid #CBD4CC', borderRadius: 10, padding: 12, fontSize: 17 };
    return (
      <div style={S.page}>
        <div style={S.bar}>
          <button style={S.back} onClick={() => setView('calendar')}>‹ ปฏิทิน</button>
          <div><h1 style={{ ...S.h, fontSize: 17 }}>เพิ่มผ้าที่ต้องส่งซัก</h1><div style={S.sub}>ผ้าเลอะ / ผ้าม่าน / ผ้าอื่นๆ นอกจากห้องที่เช็คเอาท์</div></div>
        </div>
        <div style={S.card}>
          <div style={{ fontWeight: 800, marginBottom: 8 }}>จำนวน (ชุด)</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button style={{ ...S.back, fontSize: 22, padding: '6px 18px' }} onClick={() => setDirtySets(String(Math.max(1, Number(dirtySets || 0) - 1)))}>−</button>
            <input type="number" inputMode="numeric" value={dirtySets} onChange={(e) => setDirtySets(e.target.value)} style={{ ...inp, textAlign: 'center', fontSize: 24, fontWeight: 800 }} />
            <button style={{ ...S.back, fontSize: 22, padding: '6px 18px' }} onClick={() => setDirtySets(String(Number(dirtySets || 0) + 1))}>+</button>
          </div>
          <input value={dirtyNote} onChange={(e) => setDirtyNote(e.target.value)} placeholder="เช่น ลูกค้าทำไวน์หกใส่ผ้าปู ห้อง 3" style={{ ...inp, fontSize: 15, marginTop: 10 }} />
          <button disabled={busy} style={S.bigBtn('#0F8A56')} onClick={submitDirty}>{busy ? 'กำลังส่ง…' : 'บันทึก'}</button>
        </div>
      </div>
    );
  }

  // ---------- รายละเอียดใบจ่าย (รายการงาน + หักค่าเสียหาย) ----------
  if (view === 'payout' && payoutId) {
    return <PayoutDetail cw={cw} S={S} payoutId={payoutId} onBack={() => { setPayoutId(null); setView('wallet'); }} />;
  }

  // ---------- ประวัติรับเงิน ----------
  if (view === 'wallet') {
    const person = balances.find((p) => p.cleaner.id === walletOf);
    const ST = { requested: ['รอโอน (ขอเบิก)', '#B26B0E'], draft: ['รอโอน (ปิดรอบ)', '#2C6DAF'], paid: ['โอนแล้ว', '#0F8A56'] };
    return (
      <div style={S.page}>
        <div style={S.bar}>
          <button style={S.back} onClick={() => setView('calendar')}>‹ ปฏิทิน</button>
          <div><h1 style={{ ...S.h, fontSize: 17 }}>ประวัติรับเงิน{person ? ` · ${person.cleaner.firstname}` : ''}</h1><div style={S.sub}>20 รายการล่าสุด</div></div>
        </div>
        {payouts.length === 0 && <div style={{ ...S.card, textAlign: 'center', color: '#8A938B' }}>ยังไม่มีประวัติ</div>}
        {payouts.map((x) => {
          const [label, color] = ST[x.status] || [x.status, '#8A938B'];
          return (
            <div key={x.id} style={{ ...S.card, cursor: 'pointer' }} onClick={() => { setPayoutId(x.id); setView('payout'); }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 20, fontWeight: 800 }}>{baht(x.total_amount)}</div>
                  <div style={{ fontSize: 12, color: '#586159' }}>
                    {x.line_count} งาน{x.period_start ? ` · ${moment(x.period_start).format('D MMM')} - ${moment(x.period_end).format('D MMM')}` : ''}
                  </div>
                  {x.paid_at && <div style={{ fontSize: 12, color: '#586159' }}>โอนเมื่อ {moment(x.paid_at).format('D MMM HH:mm')}</div>}
                  {Number(x.adjust_amount || 0) !== 0 && <div style={{ fontSize: 12, color: '#586159' }}>{Number(x.adjust_amount) > 0 ? 'เพิ่ม' : 'หัก'} {baht(Math.abs(x.adjust_amount))}{x.adjust_note ? ` (${x.adjust_note})` : ''}</div>}
                </div>
                <span style={S.pill(color)}>{label}</span>
              </div>
              {x.slip_url && <a href={x.slip_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ display: 'block', marginTop: 8, fontSize: 14, fontWeight: 700, color: '#2C6DAF' }}>🧾 ดูสลิป</a>}
              <div style={{ marginTop: 6, fontSize: 13, fontWeight: 700, color: '#2C6DAF' }}>ดูรายละเอียด ›</div>
            </div>
          );
        })}
      </div>
    );
  }

  // ---------- CALENDAR ----------
  if (view === 'calendar') {
    return (
      <div style={S.page}>
        <style>{`.react-calendar{width:100%!important;max-width:100%;border:none;font-family:inherit}`}</style>
        <div style={{ marginBottom: 10 }}>
          <h1 style={S.h}>สวัสดีค่ะ พี่{me.firstname} 🧹</h1>
          <div style={S.sub}>ปฏิทินงานของฉัน — แตะวันที่มีตัวเลขเพื่อดูรายละเอียด</div>
        </div>
        {walletPeople.map((p) => {
          const req = (p.open_payouts || []).find((x) => x.status === 'requested');
          const draft = (p.open_payouts || []).find((x) => x.status === 'draft');
          return (
            <div key={p.cleaner.id} style={{ ...S.card, borderColor: '#0F8A56', background: '#F1F7F3' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: '#586159' }}>💰 ยอดสะสม{walletPeople.length > 1 || !p.is_me ? ` · ${p.cleaner.firstname}` : ''}</div>
                  <div style={{ fontSize: 26, fontWeight: 800 }}>{baht(p.available)}</div>
                  {p.pending_approval > 0 && <div style={{ fontSize: 12, color: '#B26B0E' }}>รอแอดมินตรวจ {baht(p.pending_approval)}</div>}
                  <div style={{ fontSize: 12, color: '#586159' }}>{PAY_MODE_TEXT[p.pay_mode]}{p.pay_mode === 'cycle' && p.next_cycle_date ? ` · ปิดรอบถัดไป ${moment(p.next_cycle_date).format('D MMM')}` : ''}</div>
                </div>
                <button style={{ ...S.back, color: '#586159' }} onClick={() => openWallet(p.cleaner.id)}>ประวัติ ›</button>
              </div>
              {req && <div style={{ fontSize: 14, fontWeight: 700, color: '#B26B0E', marginTop: 8 }}>⏳ ขอเบิกแล้ว {baht(req.total_amount)} · รอแอดมินโอน</div>}
              {draft && <div style={{ fontSize: 14, fontWeight: 700, color: '#2C6DAF', marginTop: 8 }}>🗓️ ปิดรอบแล้ว {baht(draft.total_amount)} · รอแอดมินโอน</div>}
              {p.pay_mode === 'on_request' && p.available > 0 && (
                <button disabled={busy} style={{ ...S.bigBtn('#0F8A56'), padding: 14, fontSize: 18 }} onClick={() => withdraw(p)}>
                  {req ? `➕ เพิ่มยอดที่ขอเบิก (${baht(p.available)})` : `🙋 ขอเบิกเงิน ${baht(p.available)}`}
                </button>
              )}
            </div>
          );
        })}
        <Calendar
          locale="th-TH"
          activeStartDate={activeMonth}
          onActiveStartDateChange={({ activeStartDate }) => activeStartDate && setActiveMonth(activeStartDate)}
          onClickDay={(d) => { const ds = moment(d).format('YYYY-MM-DD'); if (calMap[ds]) openDay(ds); }}
          tileContent={({ date, view: v }) => {
            if (v !== 'month') return null;
            const ds = moment(date).format('YYYY-MM-DD');
            const info = calMap[ds];
            if (!info || (info.occ === 0 && info.checkouts === 0)) return null;
            // จุดเล็ก: ส้ม = เข้าใหม่ (เตรียมห้อง) · เทา = พักต่อ · ฟ้า = เช็คเอาท์ (เก็บห้อง)
            const dot = (c, key) => <span key={key} style={{ width: 6, height: 6, borderRadius: 999, background: c, display: 'inline-block' }} />;
            return <div style={{ marginTop: 3, display: 'flex', gap: 3, justifyContent: 'center' }}>
              {info.checkouts > 0 && dot('#2C6DAF', 'co') /* เช็คเอาท์ก่อน (ต้องเก็บห้องคนเก่าก่อนเตรียมห้องใหม่) */}
              {info.occ > 0 && dot(info.arrivals > 0 ? '#B26B0E' : '#8A938B', 'occ')}
            </div>;
          }}
          value={null}
        />
        <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: '6px 14px', fontSize: 12, color: '#586159' }}>
          {[['เข้าใหม่', '#B26B0E'], ['พักต่อ', '#8A938B'], ['เช็คเอาท์', '#2C6DAF']].map(([l, c]) => (
            <span key={l}><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 9, background: c, marginRight: 5 }} />{l}</span>
          ))}
        </div>
        {me.deposit_enabled && (
          <button style={{ ...S.back, width: '100%', marginTop: 18, padding: 12, color: '#B26B0E' }} onClick={() => { setUrlParams({ view: 'deposit', job: null }); setView('deposit'); }}>
            💵 เงินประกัน · รหัสประตู
          </button>
        )}
        {me.can_manage_deposit && (
          <button style={{ ...S.back, width: '100%', marginTop: 10, padding: 12, color: '#0F8A56' }} onClick={() => { setUrlParams({ view: 'deposit-manage', job: null }); setView('deposit-manage'); }}>
            💸 คืนเงินประกัน
          </button>
        )}
        {me.maintenance_enabled && (
          <button style={{ ...S.back, width: '100%', marginTop: 18, padding: 12, color: '#CD3E33' }} onClick={() => { setUrlParams({ view: 'maintenance', job: null }); setView('maintenance'); }}>
            🛠 แจ้งปัญหา / ของเสีย
          </button>
        )}
        {me.stock_enabled && (
          <button style={{ ...S.back, width: '100%', marginTop: 18, padding: 12, color: '#B26B0E' }} onClick={() => { setUrlParams({ view: 'stock', tab: 'report', job: null }); setView('stock'); }}>
            📦 แจ้งของเสีย/หาย
          </button>
        )}
        {me.stock_enabled && (
          <button style={{ ...S.back, position: 'relative', width: '100%', marginTop: 10, padding: 12, color: '#CD3E33' }} onClick={() => { setUrlParams({ view: 'stock', tab: 'mine', job: null }); setView('stock'); }}>
            🧾 รายการหักของฉัน
            {Number(me.stock_pending_ack) > 0 && (
              <span style={{ marginLeft: 8, display: 'inline-block', minWidth: 22, padding: '2px 7px', borderRadius: 99, background: '#CD3E33', color: '#fff', fontSize: 13, fontWeight: 800 }}>{me.stock_pending_ack}</span>
            )}
          </button>
        )}
        {me.laundry_enabled && (
          <button style={{ ...S.back, width: '100%', marginTop: 18, padding: 12, color: '#2C6DAF' }} onClick={() => setView('laundry-add')}>
            🧺 + มีผ้าเลอะ/ผ้าอื่นต้องส่งซัก
          </button>
        )}
      </div>
    );
  }

  // ---------- DAY ----------
  if (view === 'day') {
    return (
      <div style={S.page}>
        <div style={S.bar}>
          <button style={S.back} onClick={() => setView('calendar')}>‹ ปฏิทิน</button>
          <div><h1 style={{ ...S.h, fontSize: 17 }}>งานวันที่ {moment(selectedDate).format('D MMM')}</h1><div style={S.sub}>เฉพาะห้องของฉัน</div></div>
        </div>
        {dayBookings.length === 0 && <div style={{ ...S.card, textAlign: 'center', color: '#8A938B' }}>ไม่มีงานวันนี้ (ไม่มีเข้าพัก/เช็คเอาท์)</div>}
        {dayBookings.map((b) => {
          const checkoutDate = moment(b.end_date).add(1, 'days').format('YYYY-MM-DD'); // วันเช็คเอาท์จริง = end_date + 1
          const isCheckoutDay = selectedDate === checkoutDate; // วันนี้แขกออก = มาเก็บห้อง
          const isArrival = !isCheckoutDay && b.start_date === selectedDate;   // แขกเข้าวันนี้ = ต้องเตรียมห้อง
          const isLastNight = !isCheckoutDay && b.end_date === selectedDate;   // คืนสุดท้าย (ออกพรุ่งนี้)
          const guestQty = Number(b.guest_qty || 0);
          const addQty = Number(b.additional_guest_qty || 0) + Number(b.additional_baby_qty || 0);

          // ---- การ์ดวันเช็คเอาท์ (เก็บห้องหลังแขกออก) — ข้อมูลแจ้งเตือน ไม่มี job แยกให้เปิด ----
          if (isCheckoutDay) {
            return (
              <div key={`co-${b.id}`} style={{ ...S.card, borderColor: '#2C6DAF', background: '#EEF4FB' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 18, fontWeight: 800 }}>{b.room?.name_th}</div>
                    <div style={{ fontSize: 14, color: '#2C6DAF', fontWeight: 800, marginTop: 2 }}>🧹 เก็บห้องหลังเช็คเอาท์</div>
                    <div style={{ fontSize: 12, color: '#586159', marginTop: 3 }}>
                      🔑 เข้า {moment(b.start_date).format('D MMM')} &nbsp;→&nbsp; 🧳 ออก {moment(checkoutDate).format('D MMM')}
                    </div>
                    <div style={{ fontSize: 12, color: '#8A938B', marginTop: 2 }}>แขกออกวันนี้ · {guestQty} ท่าน</div>
                  </div>
                  <span style={S.pill('#2C6DAF')}>เก็บห้อง</span>
                </div>
                {b.cleaner_custom_text && <div style={{ fontSize: 13, color: '#B26B0E', marginTop: 6 }}>❗️ {b.cleaner_custom_text}</div>}
              </div>
            );
          }

          // ---- การ์ดวันเข้าพัก (arrival) หรือ วันพักต่อ (midstay) ----
          const checkoutStr = moment(checkoutDate).format('D MMM');
          const isStaying = !isArrival;   // start < วันนี้ <= end -> วันบริการระหว่างพัก (midstay)
          const job = (b.cleaner_jobs || []).find((j) => j.service_date === selectedDate); // งานของวันนี้ (arrival/midstay)
          const st = job ? jobStatusOf(job) : null;
          const jobDone = job && ['guest_checkedin', 'pending_approval', 'approved', 'paid'].includes(job.status);
          const accent = isStaying ? '#0F8A56' : '#DBE2DC';
          return (
            <div key={b.id} style={{ ...S.card, borderColor: accent }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>{b.room?.name_th}</div>
                  {isStaying && <div style={{ fontSize: 14, color: '#0F8A56', fontWeight: 800, marginTop: 2 }}>🧹 บริการระหว่างพัก{job && job.service_level ? ` · ${midstayLevelLabel(job.service_level)}` : ''}</div>}
                  <div style={{ fontSize: 13, color: '#586159', marginTop: 2 }}>
                    👥 {guestQty} ท่าน{addQty > 0 && <span style={{ color: '#B26B0E', fontWeight: 700 }}> ❗️เสริม {addQty}</span>}
                  </div>
                  {b.contact_name && <div style={{ fontSize: 12, color: '#8A938B', marginTop: 2 }}>👤 {b.contact_name} {b.contact_tel_1 || ''}</div>}
                  <div style={{ fontSize: 12, color: '#586159', marginTop: 3 }}>
                    🔑 เข้า {moment(b.start_date).format('D MMM')} &nbsp;→&nbsp; 🧳 ออก {checkoutStr}
                  </div>
                </div>
                {st ? <span style={S.pill(st.color)}>{st.label}</span>
                  : <span style={S.pill('#8A938B')}>ยังไม่เปิดงาน</span>}
              </div>

              {/* ป้ายบอกว่าวันที่เลือก เกิดอะไรกับ booking นี้ */}
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                {isArrival && <span style={{ ...S.pill('#B26B0E'), fontSize: 12 }}>🔑 เข้าพักวันนี้</span>}
                {isStaying && isLastNight && <span style={{ ...S.pill('#2C6DAF'), fontSize: 12 }}>🧳 คืนสุดท้าย · ออกพรุ่งนี้</span>}
                {isStaying && !isLastNight && <span style={{ ...S.pill('#8A938B'), fontSize: 12 }}>😴 แขกพักอยู่</span>}
              </div>
              {b.cleaner_custom_text && <div style={{ fontSize: 13, color: '#B26B0E', marginTop: 6 }}>❗️ {b.cleaner_custom_text}</div>}

              {job && job.is_helper && <div style={{ ...S.pill('#2C6DAF'), fontSize: 12, marginTop: 6 }}>👷 งานช่วย (ทีมหลักเป็นคนส่งงาน)</div>}
              {/* ปุ่มเปิดงาน (เฉพาะเมื่อมี job แล้ว) */}
              {job && (
                <button style={{ ...S.bigBtn(jobDone ? '#586159' : '#0F8A56'), padding: 12, fontSize: 15, marginTop: 12 }}
                  onClick={() => openJob(job.id)}>
                  {job.is_helper ? 'ดูรายละเอียดงาน ›' : jobDone ? 'ดูรายละเอียด / ประวัติ ›' : (isStaying ? 'เปิดบันทึกงาน (เลือกระดับ) ›' : 'เปิดเช็คห้อง ›')}
                </button>
              )}
              {job && !job.is_helper && job.status === 'pending' && isArrival && (
                <button style={{ ...S.bigBtn('#2C6DAF'), padding: 12, fontSize: 15, marginTop: 8 }}
                  onClick={() => acknowledge(job.id)}>รับทราบ</button>
              )}
              {!job && (
                <div style={{ fontSize: 12, color: '#8A938B', marginTop: 10, textAlign: 'center' }}>
                  {isArrival ? 'ระบบจะเปิดงานให้เมื่อใกล้ถึงวันเข้าพัก' : 'ระบบจะเปิดงานให้ตอนเช้า (08:00)'}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ---------- งานช่วย (คนช่วยเพิ่ม/คนนอก) -- ดูอย่างเดียว ทีมหลักเป็นคนส่งงาน ----------
  if (view === 'job' && phase === 'helper' && job) {
    const hb = job.booking || {};
    const mine = (job.members || []).find((m) => m.cleaner_id === me.id) || {};
    const time = job.hotel?.cleaner_checkin_time ? job.hotel.cleaner_checkin_time.substring(0, 5) : null;
    const done = ['room_ready', 'guest_checkedin', 'pending_approval', 'approved', 'paid'].includes(job.status);
    const myStatus = mine.payment_status === 'paid' ? ['💰 จ่ายแล้ว', '#0F8A56']
      : mine.payment_status === 'void' ? ['ทีมแจ้งว่าไม่ได้มา', '#CD3E33']
        : done ? ['ทีมยืนยันแล้ว · รอแอดมินโอน', '#B26B0E'] : ['ยังไม่ถึงวันงาน / กำลังทำ', '#8A938B'];
    return (
      <div style={S.page}>
        <div style={S.bar}>
          <button style={S.back} onClick={() => openDay(selectedDate)}>‹ งานวันนั้น</button>
          <div><h1 style={{ ...S.h, fontSize: 17 }}>👷 งานช่วย · {job.room?.name_th}</h1><div style={S.sub}>ทีมแม่บ้านเป็นคนส่งงาน · คุณไม่ต้องกดอะไร</div></div>
        </div>
        <div style={{ ...S.card, background: '#F1F7F3' }}>
          <div style={{ fontSize: 16, fontWeight: 800 }}>📅 {moment(job.service_date).format('D MMM YYYY')}</div>
          <div style={{ fontSize: 14, color: '#586159', marginTop: 4 }}>
            {job.job_type === 'midstay' ? 'ทำความสะอาดระหว่างพัก' : `เตรียมห้องรับลูกค้า${time ? ` · ให้เสร็จก่อน ${time} น.` : ''}`}
          </div>
          {job.hotel?.name_th && <div style={{ fontSize: 14, color: '#586159', marginTop: 2 }}>🏡 {job.hotel.name_th}</div>}
          <div style={{ fontSize: 14, color: '#586159', marginTop: 2 }}>👥 ผู้เข้าพัก {Number(hb.guest_qty || 0) + Number(hb.additional_guest_qty || 0) + Number(hb.additional_baby_qty || 0)} ท่าน</div>
          {hb.cleaner_custom_text && <div style={{ fontSize: 14, color: '#B26B0E', marginTop: 4 }}>❗️ {hb.cleaner_custom_text}</div>}
        </div>
        <div style={S.card}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>ทีมวันนี้</div>
          {(job.members || []).map((m) => (
            <div key={m.cleaner_id} style={{ padding: '4px 0', fontSize: 15 }}>
              {m.is_extra ? '👷' : '🧹'} {m.cleaner?.firstname}{m.is_lead ? ' (หัวหน้า)' : ''}{m.cleaner_id === me.id ? ' — คุณ' : ''}
            </div>
          ))}
        </div>
        <div style={S.card}>
          <div style={{ fontSize: 13, color: '#586159' }}>ค่าจ้างงานนี้</div>
          <div style={{ fontSize: 26, fontWeight: 800 }}>{baht(mine.payment_status === 'paid' ? mine.paid_amount : mine.rate_snapshot)}</div>
          <span style={S.pill(myStatus[1])}>{myStatus[0]}</span>
          {mine.payment_slip_url && <a href={mine.payment_slip_url} target="_blank" rel="noreferrer" style={{ display: 'block', marginTop: 8, fontSize: 14, fontWeight: 700, color: '#2C6DAF' }}>🧾 ดูสลิป</a>}
        </div>
      </div>
    );
  }

  // ---------- JOB (รายละเอียด + ประวัติ + checklist) ----------
  const isMidstay = phase === 'midstay';
  const isPrep = phase !== 'checkin';
  const b = job?.booking || {};
  const guestQty = Number(b.guest_qty || 0);
  const additionalQty = Number(b.additional_guest_qty || 0) + Number(b.additional_baby_qty || 0);
  const totalQty = guestQty + additionalQty;
  const bNote = b.cleaner_custom_text || '';
  const history = job?.checklists || [];

  // ช่องรูปของ 1 ข้อ -- ข้อบังคับถ่ายรูป (โชว์เสมอ) หรือข้อที่แจ้งปัญหา (ไม่บังคับ)
  const renderPhotos = (it, c) => {
    const photos = c.photos || [];
    const up = uploading[it.id] || 0;
    const left = MAX_PHOTOS - photos.length - up;
    const missing = it.require_photo && photos.length === 0;
    return (
      <div style={{ marginTop: 10 }}>
        {it.require_photo && (
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6, color: missing ? (triedSubmit ? '#CD3E33' : '#586159') : '#0F8A56' }}>
            {missing ? '📷 ข้อนี้ต้องถ่ายรูป (อย่างน้อย 1 รูป)' : `📷 ถ่ายแล้ว ${photos.length} รูป ✓`}
          </div>
        )}
        {photos.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 8 }}>
            {photos.map((u) => (
              <div key={u} style={{ position: 'relative' }}>
                <img src={u} alt="" style={{ display: 'block', width: '100%', height: 92, objectFit: 'cover', borderRadius: 10 }} />
                <button onClick={() => removePhoto(it.id, u)} aria-label="ลบรูป"
                  style={{ position: 'absolute', top: 4, right: 4, width: 32, height: 32, borderRadius: 16, border: 'none', background: 'rgba(0,0,0,.6)', color: '#fff', fontSize: 16, fontWeight: 800, lineHeight: '32px', padding: 0 }}>✕</button>
              </div>
            ))}
          </div>
        )}
        {up > 0 && <div style={{ textAlign: 'center', fontSize: 15, fontWeight: 700, color: '#2C6DAF', padding: 8 }}>⏳ กำลังส่งรูป… อย่าเพิ่งปิดหน้านี้</div>}
        {left > 0 && (
          <label style={{ display: 'block', border: `2px dashed ${missing && triedSubmit ? '#CD3E33' : '#CBD4CC'}`, borderRadius: 12, padding: 16, fontSize: 17, fontWeight: 800, color: '#1A211D', textAlign: 'center', background: '#fff' }}>
            {photos.length ? `📷 ถ่ายเพิ่ม (ได้อีก ${left} รูป)` : '📷 แตะเพื่อถ่ายรูป'}
            <input type="file" accept="image/*" multiple style={{ display: 'none' }}
              onChange={(e) => { onPickPhotos(it.id, e.target.files); e.target.value = ''; }} />
          </label>
        )}
        {left <= 0 && up === 0 && <div style={{ textAlign: 'center', fontSize: 13, color: '#8A938B' }}>ครบ {MAX_PHOTOS} รูปแล้ว</div>}
      </div>
    );
  };
  return (
    <div style={S.page}>
      <div style={S.bar}>
        <button style={S.back} onClick={() => openDay(selectedDate)}>‹ งานวันนั้น</button>
        <div>
          <h1 style={{ ...S.h, fontSize: 17 }}>{job?.room?.name_th}{jobDone ? '' : ` · ${isMidstay ? 'บริการระหว่างพัก' : isPrep ? 'เตรียมห้อง' : 'ต้อนรับลูกค้า'}`}</h1>
          <div style={S.sub}>{jobDone ? 'งานนี้ส่งแล้ว — ดูรายละเอียด/ประวัติ' : (isMidstay ? 'เลือกระดับงานที่ทำ แล้วกดส่ง' : isPrep ? 'แตะ "ไม่" เฉพาะข้อที่มีปัญหา' : 'ทำครบแล้วกดยืนยัน')}</div>
        </div>
      </div>

      {/* รายละเอียดการเข้าพัก */}
      {job?.booking && (
        <div style={{ ...S.card, marginBottom: 12, background: '#F1F7F3' }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>
            👥 ผู้เข้าพัก {guestQty} ท่าน
            {additionalQty > 0 && <span style={{ color: '#B26B0E' }}> ❗️เสริม {additionalQty} (รวม {totalQty})</span>}
          </div>
          {b.contact_name && <div style={{ fontSize: 14, color: '#586159', marginTop: 4 }}>👤 {b.contact_name} {b.contact_tel_1 || ''}</div>}
          {bNote && <div style={{ fontSize: 14, color: '#B26B0E', marginTop: 4 }}>❗️ {bNote}</div>}
        </div>
      )}

      {/* midstay: ระดับที่ทำ (ส่งแล้ว) / ตัวเลือกระดับ (ยังไม่ส่ง) */}
      {isMidstay && jobDone && (
        <div style={{ ...S.card, marginBottom: 10, background: '#F1F7F3' }}>
          <div style={{ fontWeight: 800 }}>🧹 ระดับที่ทำ: {midstayLevelLabel(job?.service_level)}</div>
          {job?.linen_change && <div style={{ fontSize: 13, color: '#586159', marginTop: 4 }}>🧺 เปลี่ยนผ้า: {linenLabel(job.linen_change)}</div>}
          {job?.cleaner_note && <div style={{ fontSize: 13, color: '#586159', marginTop: 4 }}>📝 {job.cleaner_note}</div>}
        </div>
      )}
      {isMidstay && !jobDone && (
        <div style={{ ...S.card, marginBottom: 10 }}>
          <div style={{ fontWeight: 800, marginBottom: 10 }}>เลือกระดับงานที่ทำ</div>
          {MIDSTAY_LEVELS.map((lv) => {
            const on = midstayLevel === lv.v;
            return (
              <button key={lv.v} onClick={() => setMidstayLevel(lv.v)}
                style={{ boxSizing: 'border-box', width: '100%', textAlign: 'left', border: on ? '2px solid #0F8A56' : '1.5px solid #DBE2DC', background: on ? '#EAF6EF' : '#fff', borderRadius: 12, padding: 14, marginBottom: 8, fontSize: 15, fontWeight: 700, color: '#1A211D' }}>
                <span style={{ marginRight: 8 }}>{on ? '🟢' : '⚪️'}</span>{lv.v}. {lv.label}
              </button>
            );
          })}
          <input value={midstayNote} onChange={(e) => setMidstayNote(e.target.value)} placeholder="โน้ตถึงแอดมิน เช่น ลูกค้าขอแค่เก็บขยะ"
            style={{ boxSizing: 'border-box', width: '100%', border: '1.5px solid #CBD4CC', borderRadius: 10, padding: 11, fontSize: 15, marginTop: 4 }} />
          {me.laundry_midstay && (
            <>
              <div style={{ fontWeight: 800, margin: '14px 0 8px' }}>🧺 เปลี่ยนผ้าไหม?</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {LINEN_CHANGES.map((l) => {
                  const on = linenChange === l.v;
                  return (
                    <button key={l.v} onClick={() => setLinenChange(l.v)}
                      style={{ flex: 1, border: on ? '2px solid #2C6DAF' : '1.5px solid #DBE2DC', background: on ? '#EEF4FB' : '#fff', borderRadius: 10, padding: '10px 4px', fontSize: 14, fontWeight: 700, color: '#1A211D' }}>
                      {l.label}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* ประวัติที่เช็คแล้ว (read-only + รูป) */}
      {history.length > 0 && ['prep', 'checkin', 'midstay'].map((ph) => {
        const hitems = history.filter((c) => c.phase === ph);
        if (!hitems.length) return null;
        return (
          <div key={ph} style={{ ...S.card, marginBottom: 10 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{ph === 'prep' ? '🧹 เตรียมห้อง (ส่งแล้ว)' : ph === 'checkin' ? '🛎️ ต้อนรับลูกค้า (ส่งแล้ว)' : '🧹 รายการที่ทำ (ส่งแล้ว)'}</div>
            {hitems.map((c) => (
              <div key={c.id} style={{ display: 'flex', gap: 8, padding: '6px 0', borderTop: '1px solid #EEF1EE' }}>
                <span>{c.result === 'fail' ? '⚠️' : '✅'}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: c.result === 'fail' ? '#B26B0E' : '#1A211D' }}>{c.label_snapshot}</div>
                  {c.note && <div style={{ fontSize: 13, color: '#586159' }}>{c.note}</div>}
                  {photosOf(c).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                      {photosOf(c).map((u) => <a key={u} href={u} target="_blank" rel="noreferrer"><img src={u} alt="" style={{ height: 72, width: 72, objectFit: 'cover', borderRadius: 8 }} /></a>)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      })}

      {jobDone && (
        <div style={{ textAlign: 'center', fontSize: 14, color: '#0F8A56', fontWeight: 700, marginTop: 10 }}>✅ ส่งงานเรียบร้อยแล้ว</div>
      )}

      {/* งานทีม: ใครทำงานนี้บ้าง (ค่าเริ่มต้น = ทุกคน) */}
      {teamMembers.length > 0 && (askTeam || jobDone) && (() => {
        const est = jobDone ? null : estimateShares(teamMembers, worked);
        return (
          <div style={{ ...S.card, marginBottom: 10, borderColor: '#2C6DAF' }}>
            <div style={{ fontWeight: 800, marginBottom: 4 }}>👥 วันนี้ใครทำงานนี้บ้าง</div>
            {!jobDone && <div style={{ fontSize: 13, color: '#586159', marginBottom: 8 }}>แตะชื่อคนที่ <b>ไม่ได้มา</b> เพื่อปิด{hasExtras ? ' — 👷 คนช่วยที่ไม่ได้มาจะไม่ได้ค่าจ้าง' : ' — ค่าจ้างส่วนนั้นจะไปอยู่กับคนที่ทำ'}</div>}
            {teamMembers.map((m) => {
              const locked = jobDone || m.payment_status === 'paid'; // คนช่วยที่ได้เงินไปแล้วแก้ไม่ได้
              const on = locked ? m.worked !== false : worked[m.cleaner_id] !== false;
              const amt = locked ? (m.payment_status === 'void' ? null : (m.paid_amount ?? m.share_amount)) : (m.wage_type === 'monthly' ? null : est[m.cleaner_id]);
              return (
                <button key={m.cleaner_id} disabled={locked}
                  onClick={() => setWorked((w) => ({ ...w, [m.cleaner_id]: !on }))}
                  style={{ boxSizing: 'border-box', width: '100%', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', border: on ? '2px solid #0F8A56' : '1.5px solid #DBE2DC', background: on ? '#EAF6EF' : '#F4F4F4', borderRadius: 12, padding: 14, marginBottom: 8, fontSize: 17, fontWeight: 700, color: on ? '#1A211D' : '#8A938B' }}>
                  <span style={{ fontSize: 20 }}>{on ? '✅' : '❌'}</span>
                  <span style={{ flex: 1, textDecoration: on ? 'none' : 'line-through' }}>{m.is_extra ? '👷 ' : ''}{m.cleaner?.firstname}{m.is_lead ? ' (หัวหน้า)' : ''}{m.is_extra ? ' (คนช่วย)' : ''}{m.payment_status === 'paid' ? ' · จ่ายแล้ว' : ''}</span>
                  <span style={{ fontSize: 15 }}>{on ? (amt === null ? 'รายเดือน' : baht(amt)) : 'ไม่ได้มา'}</span>
                </button>
              );
            })}
          </div>
        );
      })()}

      {!jobDone && !isMidstay && template.length === 0 && <div style={{ ...S.card, textAlign: 'center', color: '#8A938B' }}>ยังไม่มีรายการเช็ค (ตั้งค่าใน CMS)</div>}
      {!jobDone && isMidstay && midstayLevel && visibleTemplate.length === 0 && <div style={{ ...S.card, textAlign: 'center', color: '#8A938B' }}>ระดับนี้ไม่มีรายการเช็ค — กดส่งได้เลย</div>}

      {!jobDone && visibleTemplate.map((it) => {
        const c = checks[it.id] || { result: 'pass', photos: [] };
        const fail = c.result === 'fail';
        const missingPhoto = triedSubmit && it.require_photo && !(c.photos || []).length;
        return (
          <div key={it.id} id={`ck-${it.id}`} style={{ ...S.card, marginBottom: 10, borderColor: missingPhoto ? '#CD3E33' : fail ? '#B26B0E' : '#DBE2DC', background: fail ? '#FBEED4' : '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, fontSize: 17, fontWeight: 700 }}>{it.label}</div>
              <div style={S.toggleWrap}>
                <button style={S.tg(!fail, 'pass')} onClick={() => setResult(it.id, 'pass')}>✓</button>
                <button style={S.tg(fail, 'fail')} onClick={() => setResult(it.id, 'fail')}>ไม่</button>
              </div>
            </div>
            {fail && (
              <div style={{ marginTop: 10 }}>
                <input value={c.note || ''} onChange={(e) => setNote(it.id, e.target.value)} placeholder="พิมพ์สั้นๆ เช่น มดขึ้นที่นอน"
                  style={{ boxSizing: 'border-box', width: '100%', border: '1.5px solid #CBD4CC', borderRadius: 10, padding: 11, fontSize: 15 }} />
              </div>
            )}
            {(fail || it.require_photo) && renderPhotos(it, c)}
          </div>
        );
      })}

      {!jobDone && (
        <>
          {/* รูปยังไม่ครบไม่ disable ปุ่ม -- กดแล้วบอกว่าขาดข้อไหน + เลื่อนไปให้ (ปุ่มกดไม่ได้เฉยๆ แม่บ้านจะงง) */}
          <button disabled={busy || (isMidstay && !midstayLevel)}
            style={S.bigBtn(missingPhotoItems.length || anyUploading ? '#8A938B' : isMidstay ? (midstayLevel ? '#0F8A56' : '#8A938B') : (isPrep && anyFail ? '#B26B0E' : '#0F8A56'))} onClick={submit}>
            {busy ? 'กำลังส่ง…' : anyUploading ? '⏳ รอรูปส่งเสร็จ…' : missingPhotoItems.length ? `📷 ยังขาดรูป ${missingPhotoItems.length} ข้อ` : isMidstay ? (midstayLevel ? `✅ ส่งงาน (ระดับ ${midstayLevel})` : 'เลือกระดับก่อน') : isPrep ? (anyFail ? '⚠️ แจ้งปัญหา + ส่งงานห้องพร้อม' : '✅ ห้องพร้อม (ส่งงาน)') : '✅ คอนเฟิร์มส่งงาน'}
          </button>
          <div style={{ textAlign: 'center', fontSize: 12.5, color: '#8A938B', marginTop: 8 }}>
            {job?.wage_type === 'monthly'
              ? (isPrep && anyFail ? 'ปัญหาจะถูกส่งให้แอดมิน' : 'ส่งรายงานให้แอดมินในกลุ่มไลน์')
              : isMidstay ? 'แอดมินจะตรวจ + ตั้งค่าจ้างตามระดับ' : isPrep && anyFail ? 'ปัญหาจะถูกส่งให้แอดมิน — ค่าจ้างไม่ถูกตัด' : 'กดปุ่มเดียวจบ'}
          </div>
        </>
      )}
    </div>
  );
}
